import logger from '@scale/frontend-lib/logging';
import {
  BaseIcon,
  Button,
  SegmentedButtons,
  Icon,
  Modal,
  SelectMenu,
  TextArea,
  TextField,
  Typography,
} from '@scale/scaleui';
import React, { useEffect, useState } from 'react';
import { AUDIT_LEVELS_TO_ADD } from 'react-scale/components/audits/SpeedAudit/v2/constants';
import {
  useChatBulkAuditFilteredTasks,
  useCreateChatBulkAuditBatch,
  useChatBulkAuditObjectIdsWithAttempts,
} from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { useGetProjectReviewLevels } from '../../../communication/apiClient';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

const TASKS_LIMIT = 100;

const InitialTaskFilterOptions = [
  {
    id: 'Custom Filter',
    name: 'Custom Filter',
  },
];

const DEFAULT_FILTER_OPTION = InitialTaskFilterOptions[0].id;

interface FilterOption {
  id: string;
  name: string;
}
interface CustomFilter {
  primary: string;
  secondary?: string;
  tertiary?: string | string[];
  secondaryOptions?: FilterOption[];
  tertiaryOptions?: FilterOption[];
}

const CustomFilterOptions = [
  {
    id: 'Review Level',
    name: 'Review Level',
    secondaryOptions: [{ id: 'is', name: 'is' }],
    tertiaryOptions: [
      { id: '-1', name: 'Attempt' },
      { id: '0', name: 'L0' },
      { id: '1', name: 'L1' },
      { id: '4', name: 'L4' },
      { id: '10', name: 'L10' },
      { id: '12', name: 'L12' },
    ],
  },
  {
    id: 'Has been Audited',
    name: 'Has been Audited',
    secondaryOptions: [{ id: 'is', name: 'is' }],
    tertiaryOptions: [
      { id: 'true', name: 'True' },
      { id: 'false', name: 'False' },
    ],
  },
];
const DEFAULT_CUSTOM_FILTER_OPTION = CustomFilterOptions[0].id;

const SelectionOptions = {
  Filters: 'Filters',
  ObjectIDs: 'ObjectIDs',
};

const CustomCardSelection: React.FC<{
  title: string;
  description: string;
  isSelected: boolean;
  isDisabled: boolean;
  onSelection: React.MouseEventHandler<HTMLButtonElement>;
}> = ({ title, description, isSelected, isDisabled, onSelection }) => {
  return (
    <div className="mb-4">
      <Button
        onClick={onSelection}
        size="lg"
        variant={isSelected ? 'outline' : 'white'}
        disabled={isDisabled}
        className="w-full"
      >
        <div className="flex flex-row items-center h-14 space-x-4">
          <div className="flex-1 overflow-hidden">
            <Typography.Subtitle3 className="truncate">{title}</Typography.Subtitle3>
            <Typography.Caption className="truncate">{description}</Typography.Caption>
          </div>
          <BaseIcon
            className="text-inherit"
            edge="start"
            icon={[isSelected ? 'fas' : 'far', isSelected ? 'check-circle' : 'circle']}
            variant="neutral"
            size="lg"
          />
        </div>
      </Button>
      <div className="h-6"></div>
    </div>
  );
};

/* eslint-disable complexity */
const ChatBulkAuditModal: React.FC<{
  onClose: () => void;
  projectId: string;
  isOpen: boolean;
  isExternal?: boolean;
}> = ({ onClose, projectId, isOpen, isExternal = false }) => {
  const isOutlierAdmin = window.location.pathname.includes('/outlieradmin/');
  const externalUrl = isOutlierAdmin
    ? '/expert/outlieradmin/tools/chat_bulk_audit/'
    : '/remoadmin/tools/chat_bulk_audit/';
  const baseUrl = isExternal ? externalUrl : '/corp/chat_bulk_audit/';
  const [initialCustomFilterOptions, setInitialCustomFilterOptions] = useState(CustomFilterOptions);

  const [objectIds, setObjectIds] = useState('');
  const [auditName, setAuditName] = useState('');

  const [selectedSource, setSelectedSource] = useState('');

  const [selectedFilter, setSelectedFilter] = useState(DEFAULT_FILTER_OPTION);
  const [taskFilterOptions, setTaskFilterOptions] = useState(InitialTaskFilterOptions);

  const [customFilters, setCustomFilters] = useState<CustomFilter[]>([
    {
      primary: DEFAULT_CUSTOM_FILTER_OPTION,
      secondary: 'is',
      tertiary: undefined,
      secondaryOptions: initialCustomFilterOptions[0].secondaryOptions || [],
      tertiaryOptions: initialCustomFilterOptions[0].tertiaryOptions || [],
    },
  ]);

  const [sampleNumber, setSampleNumber] = useState<number | undefined>(undefined);

  const [objectIdCount, setObjectIdCount] = useState(0);

  const [shouldClaim, setShouldClaim] = useState(false);

  const { mutateAsync: fetchChatBulkAuditFilteredTasks, isLoading: isFetchingTasks } =
    useChatBulkAuditFilteredTasks({
      projectId,
      filters: customFilters,
      selectedFilter,
    });

  const {
    mutateAsync: fetchChatBulkAuditObjectIdsWithAttempts,
    isLoading: isFetchingObjectIdsWithAttempts,
    data: objectIdsWithAttemptsResponse,
  } = useChatBulkAuditObjectIdsWithAttempts();

  const { data: projectReviewLevels, isLoading: fetchingProjectReviewLevels } =
    useGetProjectReviewLevels({
      projectId,
    });

  const enableClaiming = useUnleashFeatureFlag('enable-claim-and-edit-cba', {}, false);

  const hasTasksWithoutAttempts =
    objectIdCount > 0 &&
    selectedSource === SelectionOptions.ObjectIDs &&
    objectIdsWithAttemptsResponse &&
    objectIdsWithAttemptsResponse.objectIdsWithoutAttempts &&
    objectIdsWithAttemptsResponse.objectIdsWithoutAttempts.length > 0;

  // update default filters and review level selections based on project's review levels
  useEffect(() => {
    if (!projectReviewLevels) return;

    const updatedOptions = [
      ...InitialTaskFilterOptions,
      ...AUDIT_LEVELS_TO_ADD.filter(({ level }) => projectReviewLevels.includes(level)).map(
        ({ filter }) => ({
          id: filter,
          name: filter,
        }),
      ),
    ];

    setTaskFilterOptions(updatedOptions);

    const updatedInitialCustomFilters = initialCustomFilterOptions.map(filter => {
      if (filter.id === 'Review Level') {
        const updatedTertiaryOptions = projectReviewLevels.map((level: number) => ({
          id: `${level}`,
          name: `L${level}`,
        }));

        return {
          ...filter,
          tertiaryOptions: updatedTertiaryOptions,
        };
      }
      return filter;
    });

    setInitialCustomFilterOptions(updatedInitialCustomFilters);

    setCustomFilters(currentFilters =>
      currentFilters.map(filter => {
        if (filter.primary === 'Review Level') {
          const updatedTertiaryOptions = projectReviewLevels.map((level: number) => ({
            id: `${level}`,
            name: `L${level}`,
          }));

          return {
            ...filter,
            tertiaryOptions: updatedTertiaryOptions,
          };
        }
        return filter;
      }),
    );
  }, [projectReviewLevels]);

  // hook for changes in secondary and tertiary filters to get tasks count
  useEffect(() => {
    const fetchObjectCount = async () => {
      if (selectedSource === SelectionOptions.ObjectIDs) {
        setObjectIdCount(
          objectIds
            .split(/\s+|,/)
            .map((id: string) => id.trim())
            .filter((id: string) => id).length,
        );
      } else {
        const response = await fetchChatBulkAuditFilteredTasks(undefined);
        setObjectIdCount(response.length);
      }
    };

    if (
      selectedFilter === 'Custom Filter' &&
      customFilters.some(filter => !filter.secondary || !filter.tertiary)
    ) {
      setObjectIdCount(0);
    } else {
      fetchObjectCount();
    }
  }, [selectedFilter, selectedSource, customFilters, fetchChatBulkAuditFilteredTasks]);

  const handleAddFilter = () => {
    setCustomFilters(currentFilters => {
      const primaryOption = initialCustomFilterOptions?.find(
        option => option.id === DEFAULT_CUSTOM_FILTER_OPTION,
      );
      const newFilters = [
        ...currentFilters,
        {
          primary: DEFAULT_CUSTOM_FILTER_OPTION,
          secondary: undefined,
          tertiary: undefined,
          secondaryOptions: primaryOption?.secondaryOptions || [],
          tertiaryOptions: primaryOption?.tertiaryOptions || [],
        },
      ];
      return newFilters;
    });
  };

  const handleRemoveFilter = (index: number) => {
    setCustomFilters(currentFilters => currentFilters.filter((_, i) => i !== index));
  };

  const handleSelectCustomFilterChange = (
    selectedId: string | string[],
    index: number,
    filterType: string,
  ) => {
    setCustomFilters(currentFilters =>
      currentFilters.map((filter, i) => {
        if (i === index) {
          switch (filterType) {
            case 'primary': {
              const primarySelectedId = Array.isArray(selectedId) ? selectedId[0] : selectedId; // primary will always have a single value
              const primaryOption = initialCustomFilterOptions?.find(
                option => option.id === primarySelectedId,
              );
              return {
                ...filter,
                primary: primarySelectedId,
                secondary: 'is',
                tertiary: undefined,
                secondaryOptions: primaryOption?.secondaryOptions || [],
                tertiaryOptions: primaryOption?.tertiaryOptions || [],
              };
            }
            case 'secondary': {
              const secondarySelectedId = Array.isArray(selectedId) ? selectedId[0] : selectedId; // secondary will always have a single value
              return { ...filter, secondary: secondarySelectedId };
            }
            case 'tertiary':
              return { ...filter, tertiary: selectedId };
            default:
              return filter;
          }
        }
        return filter;
      }),
    );
  };

  const isCreateEnabled: boolean = !!(
    (selectedSource === SelectionOptions.ObjectIDs && objectIds.trim() !== '') ||
    (selectedSource === SelectionOptions.Filters &&
      sampleNumber &&
      sampleNumber > 0 &&
      sampleNumber <= TASKS_LIMIT &&
      ((selectedFilter === 'Custom Filter' &&
        customFilters.every(
          filter =>
            filter.primary !== undefined &&
            filter.secondary !== undefined &&
            filter.tertiary !== undefined,
        )) ||
        selectedFilter !== 'Custom Filter'))
  );

  const { mutateAsync: createChatBulkAudit, isLoading: isCreatingChatBulkAudit } =
    useCreateChatBulkAuditBatch({
      projectId,
      auditName,
      shouldClaim,
    });

  const onCreateChatBulkAudit = async () => {
    let response: any = null;
    try {
      if (selectedSource === SelectionOptions.ObjectIDs) {
        response = await createChatBulkAudit(objectIds);
      } else if (selectedSource === SelectionOptions.Filters) {
        const objectIdsToUse = await fetchChatBulkAuditFilteredTasks(sampleNumber);
        response = await createChatBulkAudit(objectIdsToUse.join(', '));
      }
    } catch (error) {
      logger.error(error);
    } finally {
      onClose();
      if (response && response.auditBatch && response.auditBatch._id) {
        const url = `${baseUrl}${response.auditBatch._id}`;
        window.open(url, '_blank');
      }
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} theme="scale-theme" title="Create a new Bulk Audit">
      <div className="h-full flex flex-col space-y-6">
        <div className="flex-1 space-y-4">
          <Typography.Subtitle3>Audit batch name</Typography.Subtitle3>
          <TextField
            className="w-96"
            value={auditName}
            onChange={e => setAuditName(e.target.value)}
          />
        </div>
        <div className="flex-1 space-y-4">
          <Typography.Subtitle3>Select a source for your audit</Typography.Subtitle3>
          <div className="flex space-x-4 items-center">
            <CustomCardSelection
              title={'Find Tasks'}
              description={'Select custom filters for tasks to sample from'}
              isSelected={selectedSource === SelectionOptions.Filters}
              isDisabled={false}
              onSelection={() => setSelectedSource(SelectionOptions.Filters)}
            />
            <CustomCardSelection
              title={'Object IDs'}
              description={'Paste object IDs for tasks or task attempts.'}
              isSelected={selectedSource === SelectionOptions.ObjectIDs}
              isDisabled={false}
              onSelection={() => setSelectedSource(SelectionOptions.ObjectIDs)}
            />
          </div>
        </div>
        {selectedSource === SelectionOptions.Filters && (
          <>
            <div className="flex-1 space-y-4">
              <div className="flex-1 space-y-2">
                <Typography.Subtitle3>Filter Selection</Typography.Subtitle3>
                <Typography.Caption>
                  Select a quick filter from the dropdown or use custom logic
                </Typography.Caption>
              </div>
              <SelectMenu
                disabled={fetchingProjectReviewLevels || isFetchingTasks}
                options={taskFilterOptions}
                selectedOptionId={selectedFilter}
                setSelected={selectedId => setSelectedFilter(selectedId)}
              />
              {selectedFilter === 'Custom Filter' &&
                customFilters.map((filter, index) => (
                  <div key={index} className="ml-8">
                    <div className="flex flex-row items-center space-x-4">
                      <SelectMenu
                        className="w-48"
                        options={initialCustomFilterOptions}
                        selectedOptionId={filter.primary}
                        setSelected={selectedId =>
                          handleSelectCustomFilterChange(selectedId, index, 'primary')
                        }
                      />
                      <Typography.Paragraph2>is</Typography.Paragraph2>
                      {filter.primary === 'Review Level' && (
                        <SelectMenu
                          className="w-48"
                          multiple
                          options={filter.tertiaryOptions || []}
                          selectedOptionId={(filter.tertiary as string[]) || []}
                          setSelected={selectedId =>
                            handleSelectCustomFilterChange(
                              selectedId as string[],
                              index,
                              'tertiary',
                            )
                          }
                        />
                      )}
                      {filter.primary !== 'Review Level' && (
                        <SelectMenu
                          className="w-48"
                          options={filter.tertiaryOptions || []}
                          selectedOptionId={(filter.tertiary as string) || ''}
                          setSelected={selectedId =>
                            handleSelectCustomFilterChange(selectedId, index, 'tertiary')
                          }
                        />
                      )}
                      <Icon
                        className=""
                        icon={['fas', 'window-minimize']}
                        onClick={() => handleRemoveFilter(index)}
                        size="sm"
                        tooltip=""
                        variant="neutral"
                      />
                    </div>
                  </div>
                ))}
              {selectedFilter === 'Custom Filter' && (
                <Button className="ml-8" variant="primary" onClick={handleAddFilter}>
                  Add Filter
                </Button>
              )}
            </div>
            <div className="flex-1 space-y-4">
              <Typography.Subtitle3>
                Number of tasks to sample (max {TASKS_LIMIT})
              </Typography.Subtitle3>
              <TextField
                className="w-32"
                value={sampleNumber}
                onChange={e => {
                  // only update state if input is empty (to allow clearing the input) or contains only digits
                  if (e.target.value === '') {
                    setSampleNumber(undefined);
                  }
                  if (/^[0-9]+$/.test(e.target.value)) {
                    setSampleNumber(Number(e.target.value));
                  }
                }}
                placeholder="Enter a number"
              />
            </div>
          </>
        )}
        {selectedSource === SelectionOptions.ObjectIDs && (
          <div className="flex-1 space-y-4">
            <Typography.Subtitle3>Enter Object IDs</Typography.Subtitle3>
            <TextArea
              className="w-96"
              rows={8}
              value={objectIds}
              onChange={e => {
                setObjectIds(e.target.value);
                setObjectIdCount(
                  e.target.value
                    .split(/\s+|,/)
                    .map(id => id.trim())
                    .filter(id => id).length,
                );
                fetchChatBulkAuditObjectIdsWithAttempts(
                  e.target.value
                    .split(/\s+|,/)
                    .map(id => id.trim())
                    .filter(id => id),
                );
              }}
            />
          </div>
        )}
        {isFetchingTasks || isFetchingObjectIdsWithAttempts ? (
          <Typography.Subtitle3>Fetching tasks...</Typography.Subtitle3>
        ) : hasTasksWithoutAttempts ? (
          <Typography.Subtitle3>
            {objectIdsWithAttemptsResponse.objectIdsWithoutAttempts.length} object IDs have no
            attempts. Please ensure that all object IDs are valid and have attempts.
          </Typography.Subtitle3>
        ) : (
          <Typography.Subtitle3>{objectIdCount} tasks found with attempts.</Typography.Subtitle3>
        )}
        {enableClaiming && (
          <div className="flex flex-row items-center">
            <Typography.Subtitle3>Mode</Typography.Subtitle3>
            <div className="flex gap-4 mx-4">
              <SegmentedButtons
                buttons={[
                  {
                    icon: ['far', 'check-circle'],
                    id: 'justAudit',
                    label: 'Just Audit',
                  },
                  {
                    icon: ['far', 'edit'],
                    id: 'auditAndEdit',
                    label: 'Audit and Edit',
                  },
                ]}
                onSelect={id => setShouldClaim(id === 'auditAndEdit')}
                selected={shouldClaim ? 'auditAndEdit' : 'justAudit'}
              />
            </div>
          </div>
        )}
        <div className="h-[1px] bg-neutral-200 -mx-2" />
        <div className="flex justify-end">
          <Button onClick={onClose} variant="neutral">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await onCreateChatBulkAudit();
            }}
            className="ml-2"
            variant="primary"
            disabled={
              isFetchingTasks ||
              isCreatingChatBulkAudit ||
              (!isCreateEnabled && !isCreatingChatBulkAudit) ||
              objectIdCount === 0 ||
              hasTasksWithoutAttempts
            }
          >
            Create Audit
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChatBulkAuditModal;
